import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { DataGridFlexSales, useDataGridFlexSales } from '../DataGridFlexSales';

import {
  reportFilterCountryCodeSelector,
  reportConciliationDetailSelector,
  activeConciliationRulesSelector,
  conciliationMethodSelector,
} from '../../state';
import { ConciliationDetail } from '../../types';
import handlerFactory, { ConciliationsDataGridContext } from './handlerFactory';

const DataGridConfig = () => {
  const { handler } = useDataGridFlexSales<ConciliationDetail, ConciliationsDataGridContext>();

  const { t } = useTranslation();
  const title = t('conciliationDetails.title');
  const country = useSelector(reportFilterCountryCodeSelector);
  const conciliationMethod = useSelector(conciliationMethodSelector);
  const { data, loading } = useSelector(reportConciliationDetailSelector);
  const activeRules = useSelector(activeConciliationRulesSelector);

  const activeRuleCodes = useMemo(() => new Set(activeRules.map(rule => rule.code)), [activeRules]);

  const filteredData = useMemo(
    () => (data ?? []).filter(row => activeRuleCodes.has(row.error)),
    [data, activeRuleCodes]
  );
  const gridContext = useMemo(
    () => ({
      ...handler.context,
      t,
      country,
      conciliationMethod,
    }),
    [t, country, conciliationMethod]
  );

  handler.setData(filteredData);
  handler.setContext(gridContext);
  handler.setTitle(title);
  handler.setLoading(loading);

  return null;
};

const ConciliationDetailDataGrid = () => {
  const activeRules = useSelector(activeConciliationRulesSelector);

  const factoryWithRules = useCallback(
    (locale: any) => handlerFactory(locale, activeRules),
    [activeRules]
  );
  return (
    <DataGridFlexSales
      key={activeRules.map(r => r.code).join('-')}
      handlerFactory={factoryWithRules}
    >
      <DataGridConfig />
    </DataGridFlexSales>
  );
};

export default ConciliationDetailDataGrid;
